import React from "react";
import _ from "lodash";
import moment from "moment";
import { Moment } from "moment";
import { Button, ButtonProps } from "reactstrap";
import {
    DateRangePicker,
    DateRangePickerShape,
    FocusedInputShape,
    isSameDay,
} from "react-dates";
import { StylesConfig } from "react-select/lib/styles";

import { Select } from "@ui/Select";
import { WaIcon } from "../WaIcon";

export interface DateRange {
    label: string | JSX.Element;
    from: Moment;
    to: Moment;
}

interface PresetDateRangePickerOwnProps {
    presets: DateRange[];
    earliestDate: Moment;
    latestDate: Moment;
    maxDays?: number;
}

type Omit<T, K extends keyof T> = Pick<T, Exclude<keyof T, K>>;
type PickerShape = Omit<DateRangePickerShape, "focusedInput" | "onFocusChange" | "isOutsideRange">;
type Props = PresetDateRangePickerOwnProps & PickerShape;

interface PresetDateRangePickerState {
    startDate: Moment;
    endDate: Moment;
    focusedInput: FocusedInputShape;
}

interface RenderMonthElementProps {
    month: Moment;
    onMonthSelect: (currentMonth: Moment, newMonthVal: string) => void;
    onYearSelect: (currentMonth: Moment, newYearVal: string) => void;
}

interface NumberOption {
    value: number;
    label: string;
}
type SelectNumber = typeof Select<NumberOption>;
const SelectNumber = Select as SelectNumber;
interface DatesObject {
    startDate: Moment;
    endDate: Moment;
}

class PresetDateRangePicker extends React.Component<Props, PresetDateRangePickerState> {
    constructor(props: Props) {
        super(props);

        this.state = {
            startDate: props.startDate,
            endDate: props.endDate,
            focusedInput: null,
        };

        this.isOutsideRange = this.isOutsideRange.bind(this);
        this.onDatesChange = this.onDatesChange.bind(this);
        this.onFocusChange = this.onFocusChange.bind(this);
        this.onClose = this.onClose.bind(this);
        this.renderMonthElement = this.renderMonthElement.bind(this);
        this.renderDatePresets = this.renderDatePresets.bind(this);
    }

    private isOutsideRange(day: Moment) {
        const { focusedInput, startDate } = this.state;
        const { earliestDate, latestDate, maxDays } = this.props;
        return day < earliestDate || day > latestDate
            || (
                maxDays != null
                && startDate != null
                && focusedInput === "endDate"
                && day.isAfter(startDate.clone().add(maxDays, "days"),
            )
        );
    }

    private onDatesChange(dates: DatesObject) {
        this.setState(dates);
        this.props.onDatesChange(dates);
    }

    private onFocusChange(focusedInput: FocusedInputShape) {
        this.setState({ focusedInput });
    }

    private onClose(dates: DatesObject) {
        const { startDate, endDate } = dates;
        const { maxDays, onClose } = this.props;
        if (maxDays != null) {
            // If they've circumvented the validation by focusing out, force the dates to be valid.
            if (endDate != null && endDate.isAfter(startDate.clone().add(maxDays, "days"))) {
                dates = {
                    startDate,
                    endDate: startDate.clone().add(maxDays, "days"),
                };
                this.setState({ endDate: dates.endDate });
                this.props.onDatesChange(dates);
            }
        }
        if (onClose != null) {
            onClose(dates);
        }
    }

    private renderDatePresets() {
        const { presets, onClose } = this.props;
        const { startDate, endDate } = this.state;

        const buttons = _.map(presets, ({ label, from, to }, key) => {
            const isSelected = isSameDay(from, startDate) && isSameDay(to, endDate);
            const props: ButtonProps = {
                color: "primary",
                size: "sm",
                onClick: () => {
                    // Unfocus
                    this.setState({ focusedInput: null });
                    // And update the dates
                    this.onDatesChange({ startDate: from, endDate: to });
                    if (onClose != null) {
                        onClose({ startDate: from, endDate: to });
                    }
                },
            };
            if (!isSelected) {
                props.color = "secondary";
                props.outline = true;
            }
            return (
                <Button key={key} {...props}>{label}</Button>
            );
        });

        return (
            <div className="preset-date-range-picker__buttons">
                {buttons}
            </div>
        );
    }

    private renderMonthElement({ month, onMonthSelect, onYearSelect }: RenderMonthElementProps) {
        const { earliestDate, latestDate } = this.props;
        const yearsDiff = latestDate.year() - earliestDate.year();
        const months = moment.months().map((label, value) => ({ label, value }));
        const years = _.map(Array(yearsDiff + 1), (_x, i) => {
            const year = i + earliestDate.year();
            return {
                label: String(year),
                value: year,
            };
        });
        // HACK Fix for z-index issue
        const weekdays = moment.weekdaysMin(true);
        const monthOpt = _.find(months, ["value", month.month()]);
        const yearOpt = _.find(years, ["value", month.year()]);

        // Set the styles on the selects.
        const styles: StylesConfig = {
            dropdownIndicator: (provided, _state) => ({
                ...provided,
                padding: 4,
            }),
        };

        return (
            <div className="preset-date-range-picker__month">
                <div className="preset-date-range-picker__select preset-date-range-picker__select--month">
                    <SelectNumber
                        options={months}
                        value={monthOpt}
                        onChange={(m: NumberOption) => onMonthSelect(month, String(m.value))}
                        isSearchable={false}
                        isClearable={false}
                        maxMenuHeight={200}
                        styles={styles}
                    />
                </div>
                <div className="preset-date-range-picker__select preset-date-range-picker__select--year">
                    <SelectNumber
                        options={years}
                        value={yearOpt}
                        onChange={(y: NumberOption) => onYearSelect(month, String(y.value))}
                        isSearchable={false}
                        isClearable={false}
                        maxMenuHeight={200}
                        styles={styles}
                    />
                </div>
                <div className="preset-date-range-picker__days">
                    <ul>
                        {weekdays.map((day) => (
                            <li key={day}>{day}</li>
                        ))}
                    </ul>
                </div>
            </div>
        );
    }

    public render() {
        const { startDate, endDate, focusedInput } = this.state;

        // Remove the props sent to the
        const props = _.omit(this.props, [
            "startDate",
            "endDate",
            "earliestDate",
            "latestDate",
            "maxDays",
            "presets",
            "onClose",
        ]) as PickerShape;

        return (
            <div className="preset-date-range-picker">
                <DateRangePicker
                    {...props}
                    renderCalendarInfo={this.renderDatePresets}
                    renderMonthElement={this.renderMonthElement}
                    isOutsideRange={this.isOutsideRange}
                    onDatesChange={this.onDatesChange}
                    onFocusChange={this.onFocusChange}
                    onClose={this.onClose}
                    startDate={startDate}
                    endDate={endDate}
                    focusedInput={focusedInput}
                    customArrowIcon={<WaIcon name="arrow-right" />}
                />
            </div>
        );
    }
}

export {
    PresetDateRangePicker,
    Props as PresetDateRangePickerProps,
    DatesObject,
};
